// src/pages/Assessment.js
// Unified assessment page: shows form if not submitted, results + edit if submitted

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Tooltip, ResponsiveContainer
} from 'recharts';
import {
  collection, addDoc, query, where, getDocs, doc, getDoc, updateDoc,
  serverTimestamp
} from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_CATEGORIES, SCALE_LABELS, withCurrentLabels } from '../utils/surveyQuestions';
import { ROLES_BY_CATEGORY } from '../utils/roles';
import styles from './Assessment.module.css';

function scoreColor(score) {
  if (score >= 4) return '#2d6a4f';
  if (score >= 3) return '#f4a261';
  return '#e63946';
}

function scoreLabel(score) {
  if (score >= 4.5) return { label: 'Excellent', cls: 'badge-green' };
  if (score >= 3.5) return { label: 'Good', cls: 'badge-blue' };
  if (score >= 2.5) return { label: 'Needs attention', cls: 'badge-yellow' };
  return { label: 'Critical', cls: 'badge-red' };
}

function computeTeamScores(responses, categories) {
  const catScores = {};
  categories.forEach(cat => {
    const scores = [];
    responses.forEach(r => {
      cat.questions.forEach(q => {
        if (r.answers?.[q.id] != null) scores.push(r.answers[q.id]);
      });
    });
    catScores[cat.id] = scores.length
      ? +(scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2)
      : null;
  });
  return catScores;
}

function EditAssessmentModal({ isOpen, onClose, userResponse, categories, onSave }) {
  const [answers, setAnswers] = useState({});
  const [currentCat, setCurrentCat] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userResponse?.answers) setAnswers(userResponse.answers);
  }, [userResponse]);

  if (!isOpen) return null;

  const cat = categories[currentCat];

  const setAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(answers);
      onClose();
    } catch (err) {
      console.error('Failed to save:', err);
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={`card ${styles.modalInner}`}>
        <div className={styles.modalHeader}>
          <h3>Edit Assessment</h3>
          <button onClick={onClose} className={styles.closeBtn}>×</button>
        </div>

        {/* Category tabs */}
        <div className={styles.catTabs}>
          {categories.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setCurrentCat(i)}
              className={`btn ${i === currentCat ? 'btn-primary' : 'btn-secondary'} ${styles.catTabBtn}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Questions */}
        <div className={styles.questionsBlock}>
          <div
            className={styles.catDotInline}
            style={{ background: cat.color }}
          />
          <strong>{cat.label}</strong>

          <div className={styles.questionsListModal}>
            {cat.questions.map((q, qi) => (
              <div key={q.id}>
                <p className={styles.questionText}>
                  <span className={styles.questionNum}>Q{qi + 1}</span>
                  {q.text}
                </p>
                <div className="scale-group">
                  {[1, 2, 3, 4, 5].map(val => (
                    <button
                      key={val}
                      className={`scale-btn ${answers[q.id] === val ? 'selected' : ''}`}
                      onClick={() => setAnswer(q.id, val)}
                      title={SCALE_LABELS[val]}
                    >
                      {val}
                    </button>
                  ))}
                  {answers[q.id] && (
                    <span className={styles.answerLabel}>
                      {SCALE_LABELS[answers[q.id]]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className={styles.actionsRow}>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Assessment() {
  const { currentUser, userProfile, effectiveUid, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Form state
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [currentCat, setCurrentCat] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedRoles, setSelectedRoles] = useState({}); // { [roleId]: [memberName, ...] }
  const [teamMembers, setTeamMembers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Results state
  const [userResponse, setUserResponse] = useState(null);
  const [responses, setResponses] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentCat]);

  // Edit mode — opens as full page when ?edit=1
  const editMode = new URLSearchParams(location.search).get('edit') === '1';

  // Loading state
  const [loading, setLoading] = useState(true);

  const role = userProfile?.role || 'free';
  const teamId = userProfile?.teamId || null;

  useEffect(() => {
    async function init() {
      try {
        // Load custom questions and team members if on a team
        if (teamId) {
          const teamSnap = await getDoc(doc(db, 'teams', teamId));
          if (teamSnap.exists() && teamSnap.data().customQuestions) {
            setCategories(withCurrentLabels(teamSnap.data().customQuestions));
          }

          const membersSnap = await getDocs(
            query(collection(db, 'users'), where('teamId', '==', teamId))
          );
          setTeamMembers(membersSnap.docs.map(d => ({ uid: d.id, ...d.data() })));
        }

        // Check if already submitted
        const q = query(
          collection(db, 'responses'),
          where('userId', '==', effectiveUid),
          ...(teamId ? [where('teamId', '==', teamId)] : [where('teamId', '==', null)])
        );
        const snap = await getDocs(q);

        if (!snap.empty) {
          // Has submitted - load results
          const userResp = snap.docs[0];
          setUserResponse({ id: userResp.id, ...userResp.data() });

          // Load all responses for team averages
          const allQ = query(
            collection(db, 'responses'),
            ...(teamId ? [where('teamId', '==', teamId)] : [where('teamId', '==', null)])
          );
          const allSnap = await getDocs(allQ);
          setResponses(allSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    if (currentUser && userProfile) {
      init();
    }
  }, [currentUser, userProfile, teamId, effectiveUid]);

  // ─── FORM VIEW ───────────────────────────────────────────────────────────────

  const allQuestions = categories.flatMap(c => c.questions);
  const answeredCount = Object.keys(answers).length;
  const totalRequired = allQuestions.length;
  const progress = Math.round((answeredCount / totalRequired) * 100);
  const ROLES_INDEX = DEFAULT_CATEGORIES.length; // 4
  const isRolesTab = currentCat === ROLES_INDEX;
  const isLastCat = currentCat === DEFAULT_CATEGORIES.length - 1; // index 3, used for "Next" label
  const cat = categories[currentCat];

  const setAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const catComplete = () => {
    if (isRolesTab) return true;
    return cat.questions.every(q => answers[q.id] !== undefined);
  };

  const handleSubmit = async () => {
    if (isImpersonating) return;
    if (answeredCount < totalRequired) return alert('Please answer all questions before submitting.');
    setSubmitting(true);
    try {
      const docRef = await addDoc(collection(db, 'responses'), {
        userId: currentUser.uid,
        teamId: teamId || null,
        role,
        answers,
        observedRoles: selectedRoles,
        submittedAt: serverTimestamp(),
      });

      setUserResponse({ id: docRef.id, userId: currentUser.uid, teamId, role, answers, observedRoles: selectedRoles, submittedAt: new Date() });

      // Reload all responses for averages
      const allQ = query(
        collection(db, 'responses'),
        ...(teamId ? [where('teamId', '==', teamId)] : [where('teamId', '==', null)])
      );
      const allSnap = await getDocs(allQ);
      setResponses(allSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      setSubmitting(false);
    } catch (err) {
      console.error(err);
      alert('Failed to submit. Please try again.');
      setSubmitting(false);
    }
  };

  const handleEditSave = async (newAnswers) => {
    if (isImpersonating) return;
    try {
      await updateDoc(doc(db, 'responses', userResponse.id), {
        answers: newAnswers,
        updatedAt: serverTimestamp(),
      });
      setUserResponse(prev => ({ ...prev, answers: newAnswers }));
      setResponses(prev => prev.map(r =>
        r.id === userResponse.id ? { ...r, answers: newAnswers } : r
      ));
    } catch (err) {
      console.error('Error saving assessment:', err);
    }
  };

  // When entering edit mode, pre-populate answers from saved response
  useEffect(() => {
    if (editMode && userResponse?.answers) {
      setAnswers(userResponse.answers);
    }
  }, [editMode, userResponse]);

  if (loading) {
    return <p className={styles.loadingText}>Loading assessment...</p>;
  }

  // ─── EDIT VIEW (full page when ?edit=1) ──────────────────────────────────────

  if (userResponse && editMode) {
    const handleSaveAndReturn = async () => {
      setSaving(true);
      try {
        await handleEditSave(answers);
        navigate('/assessment');
      } catch (err) {
        console.error(err);
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fade-up">
        <div className="page-header">
          <h2>Edit My Assessment</h2>
          <p>Update your responses — changes are saved immediately.</p>
        </div>

        {/* Category tabs */}
        <div className={styles.catTabs}>
          {categories.map((c, i) => {
            const done = c.questions.every(q => answers[q.id] !== undefined);
            return (
              <button
                key={c.id}
                onClick={() => setCurrentCat(i)}
                className={`btn ${i === currentCat ? 'btn-primary' : 'btn-secondary'} ${styles.catTabBtn}`}
                style={done && i !== currentCat ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : undefined}
              >
                {done && i !== currentCat && '✓ '}{c.label}
              </button>
            );
          })}
        </div>

        {/* Questions */}
        <div className={`card ${styles.questionsBlock}`}>
          <div className={styles.catHeader}>
            <div className={styles.catDot} style={{ background: cat.color }} />
            <div>
              <div className={`card-title ${styles.cardTitleNoMb}`}>{cat.label}</div>
              <div className={`card-subtitle ${styles.cardTitleNoMb}`}>Rate 1–5 for each statement</div>
            </div>
          </div>

          <div className={styles.scaleLegend}>
            {Object.entries(SCALE_LABELS).map(([k, v]) => (
              <span key={k} className={styles.scaleLegendItem}>
                <strong style={{ color: 'var(--ink)' }}>{k}</strong> = {v}
              </span>
            ))}
          </div>

          <div className={styles.questionsList}>
            {cat.questions.map((q, qi) => (
              <div key={q.id}>
                <p className={styles.questionText}>
                  <span className={styles.questionNum}>Q{qi + 1}</span>
                  {q.text}
                </p>
                <div className="scale-group">
                  {[1, 2, 3, 4, 5].map(val => (
                    <button
                      key={val}
                      className={`scale-btn ${answers[q.id] === val ? 'selected' : ''}`}
                      onClick={() => setAnswer(q.id, val)}
                      title={SCALE_LABELS[val]}
                    >
                      {val}
                    </button>
                  ))}
                  {answers[q.id] && (
                    <span className={styles.answerLabel}>
                      {SCALE_LABELS[answers[q.id]]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category prev/next */}
        <div className={styles.navRow}>
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentCat(Math.max(0, currentCat - 1))}
            disabled={currentCat === 0}
          >
            ← Previous
          </button>
          {!isLastCat && (
            <button
              className="btn btn-primary"
              onClick={() => setCurrentCat(Math.min(categories.length - 1, currentCat + 1))}
            >
              Next: {categories[currentCat + 1]?.label} →
            </button>
          )}
        </div>

        {/* Bottom action bar */}
        <div className={styles.actionsRowLg}>
          <button className="btn btn-secondary" onClick={() => navigate('/assessment')} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSaveAndReturn} disabled={saving || isImpersonating}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    );
  }

  // ─── RESULTS VIEW (after submission) ──────────────────────────────────────────

  if (userResponse) {
    const teamScores = computeTeamScores(responses, categories);

    return (
      <div className="fade-up">
        <div className={styles.resultsHeaderRow}>
          <div className="page-header">
            <h2>My Assessment</h2>
            <p>Team health & culture assessment — compiled from all responses</p>
          </div>
          {['team_member', 'team_leader', 'free'].includes(role) && (
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/assessment?edit=1')}
              disabled={isImpersonating}
              title={isImpersonating ? 'Disabled while viewing as another user' : undefined}
            >
              Edit Assessment
            </button>
          )}
        </div>

        {/* Category breakdown */}
        <div className="card" style={{ marginBottom: 32 }}>
          <div className="card-title" style={{ marginBottom: 20 }}>Category Breakdown</div>
          <div
            className={styles.categoryGrid}
            style={{ gridTemplateColumns: categories.length === 4 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)' }}
          >
            {categories.map(cat => {
              const score = teamScores[cat.id];
              const label = scoreLabel(score);
              return (
                <div key={cat.id} className={styles.categoryItem}>
                  <div className={styles.categoryItemHeader}>
                    <div className={styles.catDot} style={{ background: cat.color }} />
                    <strong className={styles.categoryItemLabel}>{cat.label}</strong>
                  </div>
                  <div className={styles.scoreValue} style={{ color: scoreColor(score) }}>
                    {score?.toFixed(1) || '—'}
                    <span className={styles.scoreSuffix}>/5</span>
                  </div>
                  <div className={styles.scoreQualityLabel}>{label.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team Assessment tile */}
        {role === 'free' ? (
          <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div className={styles.lockBlurOverlay} />
            <div className={styles.lockedContent}>
              <div className="card-title" style={{ marginBottom: 4 }}>Team Assessment</div>
              <div className={styles.lockedNote}>Combined score from your whole team</div>
              <div className={styles.lockedPlaceholder} />
            </div>
            <div className={styles.lockOverlay}>
              <div className={styles.lockIcon}>🔒</div>
              <div className={styles.lockTitle}>Team Assessment</div>
              <div className={styles.lockDesc}>
                Invite your team to take the assessment and see everyone's combined score.
              </div>
              <button
                className={`btn btn-primary ${styles.unlockBtn}`}
                onClick={() => navigate('/pricing')}
              >
                Unlock Team Features →
              </button>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-title" style={{ marginBottom: 4 }}>Team Assessment</div>
            <div className={styles.submittedNote}>✓ My Assessment: Submitted</div>
            <button
              className={`btn btn-primary ${styles.viewTeamBtn}`}
              onClick={() => navigate('/team-assessment')}
            >
              See Team Combined Score →
            </button>
          </div>
        )}
      </div>
    );
  }

  // ─── FORM VIEW (before submission) ────────────────────────────────────────────

  return (
    <div className="fade-up">
      <div className="page-header">
        <h2>Meeting Assessment</h2>
        <p>Think back on your team meetings and respond to the following statements.</p>
      </div>

      {/* Progress */}
      <div className={styles.progressSection}>
        <div className={styles.progressHeader}>
          <span className={styles.progressAnswered}>
            {answeredCount} of {totalRequired} questions answered
          </span>
          <span className={styles.progressPct}>{progress}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Category tabs */}
      <div className={styles.catTabs}>
        {categories.map((c, i) => {
          const done = c.questions.every(q => answers[q.id] !== undefined);
          return (
            <button
              key={c.id}
              onClick={() => setCurrentCat(i)}
              className={`btn ${i === currentCat ? 'btn-primary' : 'btn-secondary'} ${styles.catTabBtn}`}
              style={done && i !== currentCat ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : undefined}
            >
              {done && i !== currentCat && '✓ '}{c.label}
            </button>
          );
        })}
        <button
          onClick={() => setCurrentCat(ROLES_INDEX)}
          className={`btn ${isRolesTab ? 'btn-primary' : 'btn-secondary'} ${styles.catTabBtn}`}
        >
          Roles
        </button>
      </div>

      {/* Current category questions OR Roles panel */}
      {isRolesTab ? (
        <div className={`card ${styles.questionsBlock}`}>
          <div className={styles.catHeader}>
            <div className={styles.catDot} style={{ background: '#7209b7' }} />
            <div>
              <div className={`card-title ${styles.cardTitleNoMb}`}>Roles</div>
              <div className={`card-subtitle ${styles.cardTitleNoMb}`}>All fields are optional</div>
            </div>
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--ink-muted)', marginBottom: 28, marginTop: 8 }}>
            Check any roles you observe teammates playing in your meetings.
            {teamMembers.length > 0 && ' Then select the people you see in each role.'}
          </p>

          {DEFAULT_CATEGORIES.map(defCat => {
            const roles = ROLES_BY_CATEGORY[defCat.id] || [];
            if (roles.length === 0) return null;
            return (
              <div key={defCat.id} style={{ marginBottom: 32 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
                  paddingBottom: 8, borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: defCat.color, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {defCat.label}
                  </span>
                </div>

                {roles.map((role, i) => {
                  const checked = Object.prototype.hasOwnProperty.call(selectedRoles, role.id);
                  const tagged = selectedRoles[role.id] || [];
                  return (
                    <div key={role.id} style={{
                      borderBottom: i < roles.length - 1 ? '1px solid var(--border)' : 'none',
                      paddingBottom: 12, marginBottom: 12,
                    }}>
                      <div
                        onClick={() => setSelectedRoles(prev => {
                          if (checked) { const n = { ...prev }; delete n[role.id]; return n; }
                          return { ...prev, [role.id]: [] };
                        })}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', padding: '6px 0' }}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: 4, flexShrink: 0, marginTop: 3,
                          border: `2px solid ${checked ? defCat.color : 'var(--border)'}`,
                          background: checked ? defCat.color : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s',
                        }}>
                          {checked && <span style={{ color: 'white', fontSize: '0.68rem', fontWeight: 700 }}>✓</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: checked ? 'var(--ink)' : 'var(--ink-soft)' }}>
                            {role.name}
                          </div>
                          <div style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', marginTop: 2, lineHeight: 1.5 }}>
                            {role.description}
                          </div>
                        </div>
                        {checked && (
                          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: defCat.color, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
                            Observed
                          </span>
                        )}
                      </div>

                      {checked && teamMembers.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginLeft: 32, marginTop: 6 }}>
                          {teamMembers.map(member => {
                            const label = member.displayName || member.email;
                            const isTagged = tagged.includes(label);
                            return (
                              <button
                                key={member.uid}
                                onClick={() => setSelectedRoles(prev => {
                                  const cur = prev[role.id] || [];
                                  return {
                                    ...prev,
                                    [role.id]: isTagged ? cur.filter(n => n !== label) : [...cur, label],
                                  };
                                })}
                                style={{
                                  padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
                                  border: `1.5px solid ${isTagged ? defCat.color : 'var(--border)'}`,
                                  background: isTagged ? `${defCat.color}18` : 'var(--bg-soft)',
                                  color: isTagged ? defCat.color : 'var(--ink-muted)',
                                  fontSize: '0.8rem', fontWeight: isTagged ? 600 : 400,
                                  transition: 'all 0.15s',
                                }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`card ${styles.questionsBlock}`}>
          <div className={styles.catHeader}>
            <div className={styles.catDot} style={{ background: cat.color }} />
            <div>
              <div className={`card-title ${styles.cardTitleNoMb}`}>{cat.label}</div>
              <div className={`card-subtitle ${styles.cardTitleNoMb}`}>Rate 1–5 for each statement</div>
            </div>
          </div>

          {/* Scale legend */}
          <div className={styles.scaleLegend}>
            {Object.entries(SCALE_LABELS).map(([k, v]) => (
              <span key={k} className={styles.scaleLegendItem}>
                <strong style={{ color: 'var(--ink)' }}>{k}</strong> = {v}
              </span>
            ))}
          </div>

          <div className={styles.questionsList}>
            {cat.questions.map((q, qi) => (
              <div key={q.id}>
                <p className={styles.questionText}>
                  <span className={styles.questionNum}>Q{qi + 1}</span>
                  {q.text}
                </p>
                <div className="scale-group">
                  {[1, 2, 3, 4, 5].map(val => (
                    <button
                      key={val}
                      className={`scale-btn ${answers[q.id] === val ? 'selected' : ''}`}
                      onClick={() => setAnswer(q.id, val)}
                      title={SCALE_LABELS[val]}
                    >
                      {val}
                    </button>
                  ))}
                  {answers[q.id] && (
                    <span className={styles.answerLabel}>
                      {SCALE_LABELS[answers[q.id]]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className={styles.navRowBottom}>
        <button
          className="btn btn-secondary"
          onClick={() => setCurrentCat(Math.max(0, currentCat - 1))}
          disabled={currentCat === 0}
        >
          ← Previous
        </button>

        {isRolesTab ? (
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting || answeredCount < totalRequired || isImpersonating}
          >
            {submitting ? <><span className="spinner" /> Submitting...</> : 'Submit assessment →'}
          </button>
        ) : (
          <button
            className="btn btn-primary"
            onClick={() => setCurrentCat(currentCat + 1)}
            disabled={!catComplete()}
          >
            Next: {isLastCat ? 'Roles' : categories[currentCat + 1]?.label} →
          </button>
        )}
      </div>
    </div>
  );
}
